import { FormlyFieldConfig } from '@ngx-formly/core';
import { merge } from 'lodash';

import { Injectable } from '@angular/core';

import { ISnListQueryParameters } from '@lib/api';
import { SnUser } from '@lib/api/entities';
import { SnGroupsAPI } from '@lib/api/services';
import { SnUsersAPI } from '@lib/api/services';
import { ISnHttpSelectFormFieldConfig } from '@lib/form/http-select';
import { ISnInputFormFieldConfig } from '@lib/form/input';
import { ISnMobilePhoneFormFieldConfig } from '@lib/form/mobile-phone';
import { ISnPlacesAutocompleteFormFieldConfig } from '@lib/form/places-autocomplete';
import { FsPolicyGroup } from '@lib/policy-groups';
import { TranslationService } from '@lib/translation';
import { t } from '@lib/translation/helpers';

@Injectable({ providedIn: 'root' })
export class SnUserFieldsGeneratorService {
  constructor(
    private readonly _usersAPI: SnUsersAPI,
    private readonly _groupsAPI: SnGroupsAPI,
    private readonly _translationService: TranslationService,
  ) {}

  public getUserSelectField(
    fieldConfig?: Partial<ISnHttpSelectFormFieldConfig<SnUser>>,
  ): ISnHttpSelectFormFieldConfig<SnUser> {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig<SnUser> = {
      key: 'user',
      type: 'http-select',
      templateOptions: {
        label: 'User',
        valueProp: 'id',
        labelProp: 'fullName',
        fetchDataFn: (params: ISnListQueryParameters) => this._usersAPI.getUserList(params),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getFirstNameField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'firstName',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'common.firstName',
        'common.enterFirstName',
        {
          blur: (field) => this._trimControl(field),
        },
      ),
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getLastNameField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'lastName',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'common.lastName',
        'common.enterLastName',
        {
          blur: (field) => this._trimControl(field),
        },
      ),
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getBadgeField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'badge',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'common.badge',
      ),
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getTitleField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'title',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'account.dialogs.changeTitle.rank',
      ),
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getMobilePhoneField(
    fieldConfig?: Partial<ISnMobilePhoneFormFieldConfig>,
  ): ISnMobilePhoneFormFieldConfig {
    const baseFieldConfig: ISnMobilePhoneFormFieldConfig = {
      key: 'mobilePhone',
      type: 'mobile-phone',
      templateOptions: {
        label: 'Phone',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEmailField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const email = t('common.email');
    const placeholder = t('common.enterEmail');
    const unique = t('common.validation.emailAlreadyExists');
    const invalidEmail = t('common.validation.email');

    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'email',
      type: 'input',
      templateOptions: {
        appearance: 'fill',
        label: email,
        placeholder: placeholder,
        type: 'email',
        required: true,
      },
      validators: {
        validation: ['email'],
      },
      validation: {
        messages: {
          email: invalidEmail,
          unique: unique,
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getGroupsField(
    fieldConfig?: Partial<ISnHttpSelectFormFieldConfig<FsPolicyGroup>>,
  ): ISnHttpSelectFormFieldConfig<FsPolicyGroup> {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig<FsPolicyGroup> = {
      key: 'groups',
      type: 'http-select',
      templateOptions: {
        label: 'Groups',
        multiple: true,
        valueProp: 'id',
        labelProp: 'displayName',
        tooltipProp: 'description',
        infinityScroll: true,
        fetchDataFn: (params: ISnListQueryParameters) => this._groupsAPI.getGroupList(params),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPasswordField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const password = t('common.password');
    const placeholder = t('common.enterPassword');
    const minlength = t('common.validation.chooseLongerPassword');
    const mismatchedPasswords = t('common.validation.passwordsDontMatch');

    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'password',
      type: 'input',
      templateOptions: {
        type: 'password',
        label: password,
        placeholder: placeholder,
        toggleMask: true,
      },
      validation: {
        messages: {
          minlength: minlength,
          mismatchedPasswords: mismatchedPasswords,
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getCurrentPasswordField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'password',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'account.dialogs.changePassword.currentPassword.label',
        'account.dialogs.changePassword.currentPassword.placeholder',
        {
          type: 'password',
          toggleMask: true,
        },
      ),
      validation: {
        messages: {
          minlength: t('common.validation.chooseLongerPassword'),
          mismatchedPasswords: t('common.validation.passwordsDontMatch'),
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getNewPasswordField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'newPassword',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'account.dialogs.changePassword.newPassword.label',
        'account.dialogs.changePassword.newPassword.placeholder',
        {
          type: 'password',
          toggleMask: true,
        },
      ),
      validation: {
        messages: {
          minlength: t('common.validation.chooseLongerPassword'),
          mismatchedPasswords: t('common.validation.passwordsDontMatch'),
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getConfirmPasswordField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'confirmPassword',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'account.dialogs.changePassword.confirmPassword.label',
        'account.dialogs.changePassword.confirmPassword.placeholder',
        {
          type: 'password',
          toggleMask: true,
        },
      ),
      validation: {
        messages: {
          minlength: t('common.validation.chooseLongerPassword'),
          mismatchedPasswords: t('common.validation.passwordsDontMatch'),
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getInternalIdField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'officerInternalId',
      type: 'input',
      templateOptions: {
        type: 'text',
        label: fieldConfig?.templateOptions?.label || 'Internal ID',
        placeholder: fieldConfig?.templateOptions?.placeholder || 'Enter internal ID',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getVerificationCodeField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'verificationCode',
      type: 'input',
      name: 'uid',
      templateOptions: {
        type: 'tel',
        label: 'Verification Code',
        placeholder: '······',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getLoginTimeoutField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'jwtRefreshExp',
      type: 'input',
      templateOptions: {
        appearance: 'fill',
        label: fieldConfig?.templateOptions?.label || 'Login Timeout',
        description: fieldConfig?.templateOptions?.description || 'Minutes (default 120)',
        placeholder: fieldConfig?.templateOptions?.placeholder || 'Enter timeout',
        type: 'number',
        min: 15,
      },
      validation: {
        messages: {
          min: 'Should be greater than 15 minutes',
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getOrganizationField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'organization',
      type: 'input',
      templateOptions: {
        label: 'Organization',
        placeholder: 'Enter organization',
        type: 'text',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEstimatedCamerasField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'estimatedTotalCameras',
      type: 'input',
      templateOptions: {
        label: 'Estimated Cameras',
        placeholder: 'Enter Estimated Cameras',
        type: 'number',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getExternalOrganizationField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'externalOrganization',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'auth.requestAccess.organization.label',
        'auth.requestAccess.organization.placeholder',
        {
          type: 'text',
        }
      ),
      
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getExternalOrganizationTitleField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'externalOrganizationTitle',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'auth.requestAccess.title.label',
        'auth.requestAccess.title.placeholder',
        {
          type: 'text',
        }
      ),
      
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getReferrerField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'referrer',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'auth.requestAccess.referrer.label',
        'auth.requestAccess.referrer.placeholder',
        {
          type: 'text',
        }
      ),
      
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getAddressField(
    fieldConfig?: Partial<ISnPlacesAutocompleteFormFieldConfig>,
  ): ISnPlacesAutocompleteFormFieldConfig {
    const baseFieldConfig: ISnPlacesAutocompleteFormFieldConfig = {
      key: 'address',
      type: 'places-autocomplete',
      templateOptions: {
        label: 'Address',
        placeholder: 'Enter address',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getCityField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'city',
      type: 'input',
      templateOptions: {
        label: 'City',
        placeholder: 'Enter city',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getStateField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'state',
      type: 'input',
      templateOptions: {
        label: 'State',
        placeholder: 'Enter state',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getZipField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'zip',
      type: 'input',
      templateOptions: {
        label: 'ZIP',
        placeholder: 'Enter ZIP',
        type: 'number',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _trimControl(field: FormlyFieldConfig): void {
    const value = field.formControl.value;
    if (value) {
      field.formControl.setValue(value.trim(), { emitEvent: false });
    }
  }
}
